import { Container, Form, ProgressBar, Spinner, Tab, Table, Tabs, Button } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import {
  getSales,
  getSrikandiChildren,
  getSrikandiChildrenSales,
  getSrikandiSales,
  getSrikandiSummarySales,
  useSrikandiUser,
} from '../../API';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ISale, ISummarySale, IUser, SrikandiSale } from '../../@types';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { useAuth } from '../../Context/AuthContext';
import { get, getDatabase, ref } from 'firebase/database';
import { firebaseApp } from '../../index';
import { OverrideItem, Rule, RulePage } from '../../@types';
import { DEFAULT_COMMISSION_RULE } from '../../Util';

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

const formatParamDate = (date: Date) => date.toISOString().slice(0, 10).replaceAll('-', '');
const format = (date: string) => `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;

const serialiseSearchParams = (params: any) => {
  return Object.keys(params)
    .map((e) => `${e}=${params[e]}`)
    .join('&');
};

const Sales = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const start = searchParams.get('from');
  const end = searchParams.get('till');
  const startDate = start ? new Date(format(start)) : new Date();
  const endDate = end ? new Date(format(end)) : new Date();
  const [children, setChildren] = useState<IUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [isDateSelectorClosed, setIsDateSelectorClosed] = useState(true);
  const [sales, setSales] = useState<ISale[]>([]);
  const [summarySales, setSummarySales] = useState<ISummarySale[]>([]);
  const [filterDate, setFilterDate] = useState('');
  const { user: currentUser, dbUser } = useAuth();
  const { userId } = useParams();
  const user = useSrikandiUser(userId || currentUser?.uid);
  const isSummary = window.location.pathname.includes('summary');
  const [open, setOpen] = React.useState<{ [index: number]: boolean }>({});
  const [reportDate, setReportDate] = useState(new Date());
  const [rule, setRule] = useState<RulePage>(DEFAULT_COMMISSION_RULE);
  const [srikandiChildrenSales, setSrikandiChildrenSales] = useState<SrikandiSale>();

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const getStartEndDate = (d: string) => {
    const date = new Date(d);
    const start = new Date(date.getFullYear(), date.getMonth(), 2)
      .toISOString()
      .slice(0, 10)
      .replaceAll('-', '');
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1)
      .toISOString()
      .slice(0, 10)
      .replaceAll('-', '');
    return { from: start, till: end };
  };

  const handleDownload = () => {
    console.log('download sales report');

    const format = new Intl.DateTimeFormat('en-MY', { month: 'long', year: 'numeric' }).format;

    if (window.confirm(`Download selected month (${format(reportDate)}) report summary?`)) {
      window.open(
        `https://asia-southeast1-sk-srikandi.cloudfunctions.net/query-saleSummaryReport?date=${formatDate(
          reportDate,
        )}`,
        '_blank',
      );
    }
  };

  const toRulePage = (rule: Rule): RulePage => {
    return {
      ...rule,
      override: rule.override
        ? Object.keys(rule.override).map((day) => {
            const override = rule.override![day];
            let items: OverrideItem[] = [];
            if (override) {
              Object.keys(override).forEach((sku) => {
                const value = override[sku];
                if (typeof value === 'number') {
                  items.push({
                    sku,
                    commission: value,
                  });
                }
              });
            }
            return {
              day,
              items,
            };
          })
        : [],
    };
  };

  useEffect(() => {
    if (!start || !end) {
      const params = {
        from: getStartEndDate(formatDate(new Date())).from,
        till: getStartEndDate(formatDate(new Date())).till,
      };
      setSearchParams(serialiseSearchParams(params));
    }
  }, [start, end]);

  useEffect(() => {
    if (dbUser) {
      if (dbUser.role === 'admin') {
        const database = getDatabase(firebaseApp);
        const usersRef = ref(database, 'users');
        get(usersRef).then((usersSnapshot) => {
          let userList: any[] = [];
          usersSnapshot.forEach((userSnapshot) => {
            userList = userList.concat({
              id: userSnapshot.key,
              ...userSnapshot.val(),
            });
          });
          setChildren(
            userList
              .filter((user) => user.role !== 'deactivated' && user.name) // Check if user.role is not 'deactivated' and user.name is defined
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
        });
      } else {
        getSrikandiChildren(dbUser.id).then(setChildren);
      }
    }
  }, [dbUser]);

  useEffect(() => {
    const database = getDatabase(firebaseApp);
    const year = reportDate.getFullYear();
    const month = (reportDate.getMonth() + 1).toString().padStart(2, '0');
    const ruleRef = ref(database, `/commission_rule/${year + month}`);
    get(ruleRef).then((rule) => {
      if (!rule.val()) {
        return DEFAULT_COMMISSION_RULE;
      }
      setRule(toRulePage(rule.val()));
    });
  }, [reportDate]);

  const handleDatesChanged = (dates: DateObject | DateObject[]) => {
    let dateReport: Date = new Date();
    if (dates) {
      const date = (dates as DateObject).toDate();
      date.setHours(24);
      dateReport = date;
    }
    setReportDate(dateReport);
    const params = {
      from: getStartEndDate(formatDate(dateReport)).from,
      till: getStartEndDate(formatDate(dateReport)).till,
    };
    setSearchParams(serialiseSearchParams(params));
  };

  useEffect(() => {
    if (user && startDate && endDate && isDateSelectorClosed) {
      setBusy(true);
      if (isSummary) {
        getSrikandiSummarySales(user.id, formatDate(startDate), formatDate(endDate)).then(
          (data) => {
            setBusy(false);
            setSummarySales(data);
          },
        );
      } else {
        // if has userid (from url params) query just individual sales, else group sales
        const getSalesContainer = userId ? getSales : getSrikandiSales;
        getSalesContainer(user.id, formatDate(startDate), formatDate(endDate)).then((data) => {
          setBusy(false);
          setSales(data);
        });
        const getSrikandiChildrenSalesContainer = getSrikandiChildrenSales;
        getSrikandiChildrenSalesContainer(user.id, formatDate(startDate), formatDate(endDate)).then(
          (data) => {
            setBusy(false);
            setSrikandiChildrenSales(data);
          },
        );
      }
    }
  }, [userId, user, start, end, isDateSelectorClosed, isSummary]);

  const handleFilter = () => {};

  const getOrderStatus = (sale: ISale) => {
    if (sale.cancelled_at) {
      return 'Cancelled';
    }
    if (sale.fulfilled_at) {
      return 'Fulfilled';
    }
    return 'Paid';
  };

  const groupSales = (sales: ISale[]) => {
    const grouped = sales.reduce((acc, curr) => {
      // if(!curr.event_name){
      acc[curr.order_id] = (acc[curr.order_id] || []).concat(curr);
      // }
      return acc;
    }, {} as { [orderId: string]: ISale[] });
    return Object.keys(grouped).map((e) => {
      const items = grouped[e];
      // console.log(items);
      return {
        orderId: items[0].order_name || e,
        source: items[0].type,
        email: items[0].customer_email,
        itemCount: items.length,
        orderDate: items[0].order_datetime,
        orderSales: parseFloat(items[0].order_total_sale),
        orderCurrency: items[0].order_currency,
        orderStatus: getOrderStatus(items[0]),
        items,
      };
    });
  };

  if (!user) {
    return (
      <Container>
        <Spinner
          animation="border"
          role="status"
          className="text-center"
          style={{
            position: 'absolute',
            height: '80px',
            width: '80px',
            top: '50%',
            left: '50%',
            marginLeft: '-50px',
            marginTop: '-30px',
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  const groupedSales = groupSales(sales);

  const handleExpand = (index: number) => {
    setOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const renderSales = () => {
    if (busy) {
      return (
        <Container>
          <Spinner animation="border" role="status" className="text-center">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Container>
      );
    }
    if (isSummary) {
      if (summarySales.length === 0) {
        return <span>No data</span>;
      }
      let summaryTotalSale = 0;
      return (
        <Table bordered hover size="sm" responsive>
          <thead className="tbl-header">
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>Sale</th>
            </tr>
          </thead>
          <tbody className="tbl-content">
            {/* todo: item can be clicked and expanded to view order item details (exist inside .items)*/}
            {summarySales.map((e, i) => {
              summaryTotalSale += parseFloat(e.total_sale);
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{e.email}</td>
                  <td className="midTitle">{'RM' + ' ' + parseFloat(e.total_sale).toFixed(2)}</td>
                </tr>
              );
            })}
            <tr>
              <td></td>
              <td className="smallTitle">Total</td>
              <td className="bold400 centerItem">{'RM' + ' ' + summaryTotalSale.toFixed(2)}</td>
            </tr>
          </tbody>
        </Table>
      );
    }
    if (sales.length === 0) {
      return <span>No data</span>;
    }
    return (
      <Table bordered hover size="sm" responsive>
        <thead className="tbl-header">
          <tr>
            <th className="capitalize">Order ID</th>
            <th className="capitalize">Source</th>
            <th className="capitalize" style={{ width: '25%' }}>
              Email
            </th>
            <th className="capitalize" style={{ width: '7.5%' }}>
              # Items
            </th>
            <th className="capitalize">Order Status</th>
            <th className="capitalize" style={{ width: '16%' }}>
              Order Date
            </th>
            <th className="capitalize">Order Sales</th>
          </tr>
        </thead>
        <tbody className="tbl-content" style={{ cursor: 'pointer' }}>
          {/* todo: item can be clicked and expanded to view order item details (exist inside .items)*/}
          {groupedSales.map((e, index) => {
            if (open[index]) {
              return [
                <tr key={e.orderId} onClick={() => handleExpand(index)} id="orderDetail">
                  <td rowSpan={e.items.length + 1} className="bold400 centerItem">
                    {e.orderId}
                  </td>
                  <td className="centerItem">
                    {e.source === 'woocommerce' ? 'Minisite' : 'Website'}
                  </td>
                  <td className="centerItem">{e.email}</td>
                  <td className="quantity centerItem">{e.itemCount}</td>
                  <td className="centerItem">{e.orderStatus}</td>
                  <td className="centerItem">{e.orderDate}</td>
                  <td className="bold400 centerItem">{'RM' + ' ' + e.orderSales.toFixed(2)}</td>
                </tr>,
                ...e.items.map((item) => (
                  <tr key={e.orderId + item.id}>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: 'right',
                        color: 'var(--accentColor)',
                        backgroundColor: 'var(--childColor)',
                      }}
                    >
                      {item.order_item_title}
                    </td>
                    <td
                      className="centerItem"
                      style={{ color: 'var(--accentColor)', backgroundColor: 'var(--childColor)' }}
                    >
                      {'RM' + ' ' + parseFloat(item.order_item_sale).toFixed(2)}
                    </td>
                  </tr>
                )),
              ];
            }
            return (
              <tr key={e.orderId} onClick={() => handleExpand(index)}>
                <td className="bold400 centerItem">{e.orderId}</td>
                <td className="centerItem">
                  {e.source === 'woocommerce' ? 'Minisite' : 'Website'}
                </td>
                <td className="centerItem">{e.email}</td>
                <td className="quantity centerItem">{e.itemCount}</td>
                <td className="centerItem">{e.orderStatus}</td>
                <td className="centerItem">{e.orderDate}</td>
                <td className="bold400 centerItem">{'RM' + ' ' + e.orderSales.toFixed(2)}</td>
              </tr>
            );
          })}

          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td className="smallTitle centerItem">Total</td>
            <td className="smallTitle centerItem">
              {'RM' +
                ' ' +
                groupedSales
                  .reduce(
                    (acc, curr) => (curr.orderStatus !== 'Cancelled' ? acc + curr.orderSales : acc),
                    0,
                  )
                  .toFixed(2)}
            </td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const handlePipeline = () => {
    const totalGroupSale = groupedSales
      .reduce((acc, curr) => (curr.orderStatus !== 'Cancelled' ? acc + curr.orderSales : acc), 0)
      .toFixed(2);

    srikandiChildrenSales?.srikandiSale.sort((a, b) => b.total_sale - a.total_sale);

    let totalPersonalSale = 0;
    summarySales.map((e, i) => {
      totalPersonalSale += parseFloat(e.total_sale);
    });
    console.log('totalPersonalSale', totalPersonalSale);
    if (isSummary) {
      //  team summary
      // return (
      //   <div className="table-responsive">
      //     <Tabs defaultActiveKey="sale" justify>
      //       <Tab eventKey="sale" title="Srikandi Sale">
      //         <Table striped bordered hover>
      //           <thead>
      //             <tr>
      //               <th>Email</th>
      //               <th>Progress</th>
      //             </tr>
      //           </thead>
      //           <tbody>
      //             <tr>
      //               <td>{user.email}</td>
      //               <td>
      //                 <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
      //                   <ProgressBar
      //                     label={parseFloat(totalGroupSale) === 0 ? 0 : 'RM '+Math.round((parseFloat(totalGroupSale) * 100) / 100)}
      //                     variant="success"
      //                     now={Math.round((parseFloat(totalGroupSale) * 100) / 100)}
      //                     key={1}
      //                   />
      //                   <ProgressBar
      //                     label={
      //                       parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[0].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[1].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[0].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[1].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[2].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[1].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[2].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[2].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[3].min_sale - (parseFloat(totalGroupSale) * 100) / 100) : null
      //                     }
      //                     variant="warning"
      //                     now={
      //                       parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[0].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[1].min_sale ? Math.round(rule.bonus_cash.individual.tier[0].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[1].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[2].min_sale ? Math.round(rule.bonus_cash.individual.tier[1].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[2].min_sale && parseFloat(totalGroupSale) >= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[2].min_sale - (parseFloat(totalGroupSale) * 100) / 100) :
      //                       parseFloat(totalGroupSale) <= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[3].min_sale - (parseFloat(totalGroupSale) * 100) / 100) : null
      //                     }
      //                     key={2}
      //                   />
      //                 </ProgressBar>
      //               </td>
      //             </tr>
      //             {srikandiChildrenSales !== undefined
      //               ? srikandiChildrenSales.srikandiSale.map((item, index) => (
      //                   <tr key={index}>
      //                     <td>{item.email}</td>
      //                     <td>
      //                       <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
      //                         <ProgressBar
      //                           label={item.total_sale === 0 ? 0 : 'RM '+Math.round((item.total_sale * 100) / 100)}
      //                           variant="success"
      //                           now={Math.round((item.total_sale * 100) / 100)}
      //                           key={1}
      //                         />
      //                         <ProgressBar
      //                           label={
      //                             item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                             item.total_sale <= rule.bonus_cash.individual.tier[0].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[0].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[1].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[1].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[2].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[2].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[3].min_sale - (item.total_sale * 100) / 100) : null
      //                           }
      //                           variant="warning"
      //                           now={
      //                             item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                             item.total_sale <= rule.bonus_cash.individual.tier[0].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale ? Math.round(rule.bonus_cash.individual.tier[0].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[1].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale ? Math.round(rule.bonus_cash.individual.tier[1].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[2].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[2].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[3].min_sale - (item.total_sale * 100) / 100) : null
      //                           }
      //                           key={2}
      //                         />
      //                       </ProgressBar>
      //                     </td>
      //                   </tr>
      //                 ))
      //               : null}
      //           </tbody>
      //         </Table>
      //       </Tab>
      //       <Tab eventKey="emptySale" title="Srikandi Zero Sales">
      //         <Table striped bordered hover>
      //           <thead>
      //             <tr>
      //               <th>Email</th>
      //               <th>Progress</th>
      //             </tr>
      //           </thead>
      //           <tbody>
      //             {srikandiChildrenSales !== undefined
      //               ? srikandiChildrenSales.srikandiEmptySale.map((item, index) => (
      //                   <tr key={index}>
      //                     <td>{item.email}</td>
      //                     <td>
      //                       <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
      //                         <ProgressBar
      //                           label={item.total_sale === 0 ? 0 : 'RM '+Math.round((item.total_sale * 100) / 100)}
      //                           variant="success"
      //                           now={Math.round((item.total_sale * 100) / 100)}
      //                           key={1}
      //                         />
      //                         <ProgressBar
      //                           label={
      //                             item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                             item.total_sale <= rule.bonus_cash.individual.tier[0].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[0].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[1].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[1].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[2].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[2].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale ? 'RM ' +Math.round(rule.bonus_cash.individual.tier[3].min_sale - (item.total_sale * 100) / 100) : null
      //                           }
      //                           variant="warning"
      //                           now={
      //                             item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale ? 0 :  // >10000
      //                             item.total_sale <= rule.bonus_cash.individual.tier[0].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale ? Math.round(rule.bonus_cash.individual.tier[0].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[1].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale ? Math.round(rule.bonus_cash.individual.tier[1].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[2].min_sale && item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[2].min_sale - (item.total_sale * 100) / 100) :
      //                             item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale ? Math.round(rule.bonus_cash.individual.tier[3].min_sale - (item.total_sale * 100) / 100) : null
      //                           }
      //                           key={2}
      //                         />
      //                       </ProgressBar>
      //                     </td>
      //                   </tr>
      //                 ))
      //               : null}
      //           </tbody>
      //         </Table>
      //       </Tab>
      //     </Tabs>
      //   </div>
      // );
    } else if (userId && user.role === 'admin') {
      //  individual
      return (
        <div className="table-responsive">
          <Tabs
            defaultActiveKey="sale"
            // className="mb-3"
            // fill
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Tab eventKey="sale" title="Srikandi Sale">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{user.email}</td>
                    <td>
                      <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                        <ProgressBar
                          label={
                            parseFloat(totalGroupSale) === 0
                              ? 0
                              : 'RM ' + Math.round((parseFloat(totalGroupSale) * 100) / 100)
                          }
                          variant="success"
                          now={Math.round((parseFloat(totalGroupSale) * 100) / 100)}
                          key={1}
                        />
                        <ProgressBar
                          label={
                            parseFloat(totalGroupSale) >=
                            rule.bonus_cash.individual.tier[0].min_sale
                              ? 0 // >10000
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[0].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[1].min_sale
                              ? 'RM ' +
                                Math.round(
                                  rule.bonus_cash.individual.tier[0].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[1].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[2].min_sale
                              ? 'RM ' +
                                Math.round(
                                  rule.bonus_cash.individual.tier[1].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[2].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[3].min_sale
                              ? 'RM ' +
                                Math.round(
                                  rule.bonus_cash.individual.tier[2].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                rule.bonus_cash.individual.tier[3].min_sale
                              ? 'RM ' +
                                Math.round(
                                  rule.bonus_cash.individual.tier[3].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : null
                          }
                          variant="warning"
                          now={
                            parseFloat(totalGroupSale) >=
                            rule.bonus_cash.individual.tier[0].min_sale
                              ? 0 // >10000
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[0].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[1].min_sale
                              ? Math.round(
                                  rule.bonus_cash.individual.tier[0].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[1].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[2].min_sale
                              ? Math.round(
                                  rule.bonus_cash.individual.tier[1].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[2].min_sale &&
                                parseFloat(totalGroupSale) >=
                                  rule.bonus_cash.individual.tier[3].min_sale
                              ? Math.round(
                                  rule.bonus_cash.individual.tier[2].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : parseFloat(totalGroupSale) <=
                                rule.bonus_cash.individual.tier[3].min_sale
                              ? Math.round(
                                  rule.bonus_cash.individual.tier[3].min_sale -
                                    (parseFloat(totalGroupSale) * 100) / 100,
                                )
                              : undefined
                          }
                          key={2}
                        />
                      </ProgressBar>
                    </td>
                  </tr>
                  {srikandiChildrenSales !== undefined
                    ? srikandiChildrenSales.srikandiSale.map((item, index) => (
                        <tr key={index}>
                          <td>{item.email}</td>
                          <td>
                            <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                              <ProgressBar
                                label={
                                  item.total_sale === 0
                                    ? 0
                                    : 'RM ' + Math.round((item.total_sale * 100) / 100)
                                }
                                variant="success"
                                now={Math.round((item.total_sale * 100) / 100)}
                                key={1}
                              />
                              <ProgressBar
                                label={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : null
                                }
                                variant="warning"
                                now={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : undefined
                                }
                                key={2}
                              />
                            </ProgressBar>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </Tab>
            <Tab eventKey="emptySale" title="Srikandi Zero Sales">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {srikandiChildrenSales !== undefined
                    ? srikandiChildrenSales.srikandiEmptySale.map((item, index) => (
                        <tr key={index}>
                          <td>{item.email}</td>
                          <td>
                            <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                              <ProgressBar
                                label={
                                  item.total_sale === 0
                                    ? 0
                                    : 'RM ' + Math.round((item.total_sale * 100) / 100)
                                }
                                variant="success"
                                now={Math.round((item.total_sale * 100) / 100)}
                                key={1}
                              />
                              <ProgressBar
                                label={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : null
                                }
                                variant="warning"
                                now={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : undefined
                                }
                                key={2}
                              />
                            </ProgressBar>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </Tab>
          </Tabs>
        </div>
      );
    } else {
      //  team sales
      return (
        <div className="table-responsive">
          <Tabs
            defaultActiveKey="sale"
            // className="mb-3"
            // fill
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Tab eventKey="sale" title="Srikandi Sale">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {user.role !== 'admin' ? (
                    <tr>
                      <td>{user.email}</td>
                      <td>
                        <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                          <ProgressBar
                            label={
                              parseFloat(totalGroupSale) === 0
                                ? 0
                                : 'RM ' + Math.round((parseFloat(totalGroupSale) * 100) / 100)
                            }
                            variant="success"
                            now={Math.round((parseFloat(totalGroupSale) * 100) / 100)}
                            key={1}
                          />
                          <ProgressBar
                            label={
                              parseFloat(totalGroupSale) >=
                              rule.bonus_cash.individual.tier[0].min_sale
                                ? 0 // >10000
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[0].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[1].min_sale
                                ? 'RM ' +
                                  Math.round(
                                    rule.bonus_cash.individual.tier[0].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[1].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[2].min_sale
                                ? 'RM ' +
                                  Math.round(
                                    rule.bonus_cash.individual.tier[1].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[2].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[3].min_sale
                                ? 'RM ' +
                                  Math.round(
                                    rule.bonus_cash.individual.tier[2].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[3].min_sale
                                ? 'RM ' +
                                  Math.round(
                                    rule.bonus_cash.individual.tier[3].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : null
                            }
                            variant="warning"
                            now={
                              parseFloat(totalGroupSale) >=
                              rule.bonus_cash.individual.tier[0].min_sale
                                ? 0 // >10000
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[0].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[1].min_sale
                                ? Math.round(
                                    rule.bonus_cash.individual.tier[0].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[1].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[2].min_sale
                                ? Math.round(
                                    rule.bonus_cash.individual.tier[1].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                    rule.bonus_cash.individual.tier[2].min_sale &&
                                  parseFloat(totalGroupSale) >=
                                    rule.bonus_cash.individual.tier[3].min_sale
                                ? Math.round(
                                    rule.bonus_cash.individual.tier[2].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : parseFloat(totalGroupSale) <=
                                  rule.bonus_cash.individual.tier[3].min_sale
                                ? Math.round(
                                    rule.bonus_cash.individual.tier[3].min_sale -
                                      (parseFloat(totalGroupSale) * 100) / 100,
                                  )
                                : undefined
                            }
                            key={2}
                          />
                        </ProgressBar>
                      </td>
                    </tr>
                  ) : null}
                  {srikandiChildrenSales !== undefined
                    ? srikandiChildrenSales.srikandiSale.map((item, index) => (
                        <tr key={index}>
                          <td>{item.email}</td>
                          <td>
                            <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                              <ProgressBar
                                label={
                                  item.total_sale === 0
                                    ? 0
                                    : 'RM ' + Math.round((item.total_sale * 100) / 100)
                                }
                                variant="success"
                                now={Math.round((item.total_sale * 100) / 100)}
                                key={1}
                              />

                              <ProgressBar
                                label={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : null
                                }
                                variant="warning"
                                now={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : undefined
                                }
                                key={2}
                              />
                            </ProgressBar>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </Tab>
            <Tab eventKey="emptySale" title="Srikandi Zero Sales">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {srikandiChildrenSales !== undefined
                    ? srikandiChildrenSales.srikandiEmptySale.map((item, index) => (
                        <tr key={index}>
                          <td>{item.email}</td>
                          <td>
                            <ProgressBar max={rule.bonus_cash.individual.tier[0].min_sale}>
                              <ProgressBar
                                label={
                                  item.total_sale === 0
                                    ? 0
                                    : 'RM ' + Math.round((item.total_sale * 100) / 100)
                                }
                                variant="success"
                                now={Math.round((item.total_sale * 100) / 100)}
                                key={1}
                              />

                              <ProgressBar
                                label={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? 'RM ' +
                                      Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : null
                                }
                                variant="warning"
                                now={
                                  item.total_sale >= rule.bonus_cash.individual.tier[0].min_sale
                                    ? 0 // >10000
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[0].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[1].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[0].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[1].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[2].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[1].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <=
                                        rule.bonus_cash.individual.tier[2].min_sale &&
                                      item.total_sale >= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[2].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : item.total_sale <= rule.bonus_cash.individual.tier[3].min_sale
                                    ? Math.round(
                                        rule.bonus_cash.individual.tier[3].min_sale -
                                          (item.total_sale * 100) / 100,
                                      )
                                    : undefined
                                }
                                key={2}
                              />
                            </ProgressBar>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </Tab>
          </Tabs>
        </div>
      );
    }
  };

  const handleSaleUserChange = (event: any) => {
    const userId = event.target.value;
    const params = {
      from: start,
      till: end,
    };
    if (userId === 'null') {
      navigate(`/sales?${serialiseSearchParams(params)}`);
    } else if (userId === 'summary') {
      navigate(`/sales-summary?${serialiseSearchParams(params)}`);
    } else {
      navigate(`/srikandi/${userId}/sales?${serialiseSearchParams(params)}`);
    }
  };

  return (
    <Container>
      {/* User information */}
      <div className=" centerItem">
        <h2 className="header">User Details</h2>
        <br></br>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="outletSelect">
            <b className="smallTitle">Select User</b>
          </Form.Label>
          <br></br>
          <Form.Select
            id="outletSelect"
            onChange={handleSaleUserChange}
            value={userId ? userId : isSummary ? 'summary' : 'null'}
            className="selectBox centerItem"
          >
            <option value={currentUser?.uid}>Personal</option>
            <option className="text " value="null">
              Team Sale
            </option>
            <option value="summary">Team Summary</option>
            {children.map((e) => (
              <option key={e.id} value={e.id}>{`${e.name} (${e.email})`}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Table className="mobileWidth">
          <tr className="tables" style={{ borderWidth: '0px' }}>
            <td style={{ borderBottom: '0px' }}>
              <p>
                {' '}
                <b className="smallTitle">Coupon</b>
                <br></br>
                <b className="text"> {user.coupon}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Name</b>
                <br></br>
                <b className="text"> {user.name}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Email</b>
                <br></br>
                <b className="text"> {user.email}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Date Joined</b>
                <br></br>
                <b className="text"> {new Date(user.registered_at).toISOString()}</b>
              </p>
            </td>
          </tr>
        </Table>
        <div style={{ textAlign: 'center' }}>
          <DatePicker
            onlyMonthPicker
            format="MMMM YYYY"
            onChange={handleDatesChanged}
            value={reportDate}
            minDate="2022/12/01"
            style={{
              border: '1px solid var(--primaryColor)',
              height: '20px',
              width: '120px',
              textAlign: 'center',
              fontSize: '0.6rem',
            }}
          />
        </div>
        {dbUser?.role === 'admin' ? (
          <div>
            <div
              style={{
                marginTop: '30px',
                textAlign: 'center',
              }}
            >
              <Button variant="outline-dark" onClick={handleDownload}>
                Download Sales Report
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <div className=" centerItem">
        <br></br>
        {/* <h2 className="header">Total Sales</h2> */}
        {handlePipeline()}
        <br></br>
        <br></br>
        {renderSales()}
      </div>
    </Container>
  );
};

export default Sales;
